// ═══════════════════════════════════════════════════════════════
//  BUBU DUDU — BOOKING & TRACKING SYSTEM v2.0
//  Integrations: Firebase Firestore · TextMeBot WhatsApp · EmailJS
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from "react";
import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  query,
  where,
  serverTimestamp,
  onSnapshot,
  runTransaction,
  getDoc,
} from "firebase/firestore";

// ── FIREBASE CONFIG ──────────────────────────────────────────────
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyBrzaNDH7LY4S_Tm587G8m-M5sZwJojWGg",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "bubu-dudu-orders.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "bubu-dudu-orders",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "bubu-dudu-orders.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_ID || "777698426354",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:777698426354:web:c2850904fcf0b024dabf6b",
};
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

// ── TEXTMEBOT CONFIG ─────────────────────────────────────────────
const TEXTMEBOT_API_KEY = process.env.REACT_APP_TEXTMEBOT_KEY || "ogFGNJt9vGzu";
const YOUR_WHATSAPP_NUMBER = process.env.REACT_APP_WA_NUMBER || "919265802481";

// ── EMAILJS CONFIG ───────────────────────────────────────────────
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE || "service_j80x42j";
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE || "template_e9c4r48";
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_KEY || "Bzg_ouabUpbUgDm7l";

// ── DATA ─────────────────────────────────────────────────────────
const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Argentina","Armenia",
  "Australia","Austria","Azerbaijan","Bahamas","Bahrain","Bangladesh","Belarus",
  "Belgium","Belize","Bhutan","Bolivia","Brazil","Brunei","Bulgaria","Cambodia",
  "Cameroon","Canada","Chile","China","Colombia","Croatia","Cuba","Cyprus",
  "Czech Republic","Denmark","Dominican Republic","Ecuador","Egypt","El Salvador",
  "Estonia","Ethiopia","Finland","France","Georgia","Germany","Ghana","Greece",
  "Guatemala","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland",
  "Israel","Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kuwait",
  "Latvia","Lebanon","Lithuania","Luxembourg","Malaysia","Maldives","Mexico",
  "Morocco","Nepal","Netherlands","New Zealand","Nigeria","Norway","Oman",
  "Pakistan","Palestine","Panama","Paraguay","Peru","Philippines","Poland",
  "Portugal","Qatar","Romania","Russia","Saudi Arabia","Senegal","Serbia",
  "Singapore","Slovakia","South Africa","South Korea","Spain","Sri Lanka",
  "Sweden","Switzerland","Syria","Taiwan","Tanzania","Thailand","Turkey",
  "Ukraine","United Arab Emirates","United Kingdom","United States","Uruguay",
  "Uzbekistan","Venezuela","Vietnam","Yemen","Zimbabwe",
];
const EUROPEAN_COUNTRIES = [
  "Albania","Andorra","Austria","Belarus","Belgium","Bosnia","Bulgaria",
  "Croatia","Cyprus","Czech Republic","Denmark","Estonia","Finland","France",
  "Germany","Greece","Hungary","Iceland","Ireland","Italy","Latvia",
  "Lithuania","Luxembourg","Malta","Moldova","Monaco","Montenegro",
  "Netherlands","North Macedonia","Norway","Poland","Portugal","Romania",
  "Russia","Serbia","Slovakia","Slovenia","Spain","Sweden","Switzerland",
  "Ukraine","United Kingdom","Vatican"
];
const HIGH_INCOME_COUNTRIES = ["United States","Australia",...EUROPEAN_COUNTRIES];
const getPricingInfo = (country) => {
  if (!country) return null;
  if (HIGH_INCOME_COUNTRIES.includes(country))
    return { type:"quote", msg:"Price will be shared after reviewing your request" };
  if (country === "India")
    return { type:"fixed", short:"₹1,300", long:"₹3,500" };
  return { type:"fixed", short:"$60 USD", long:"$130 USD" };
};

const OCCASIONS = [
  "Proposal","Anniversary celebration","Birthday surprise","First meeting memory",
  "Long-distance love","Reunion after long time","Apology / making up after a fight",
  "Surprise gift","Travel adventure","Cute daily life moment","Wedding / engagement",
  "Other (write your own)",
];
const ACTIVITIES = [
  "Watching movies / TV together","Traveling","Bike rides / drives","Playing games",
  "Cooking together","Going on dates","Studying together","Late night talks",
  "Shopping","Cafés / food dates","Dancing / music","Other (write your own)",
];
const MOODS = ["Romantic", "Funny", "Emotional", "Cute"];
const SCENE_STYLES = ["Romantic", "Funny", "Cute", "Emotional", "Surprise"];
const LANGUAGES = ["English", "Hindi"];
const STEPS = ["Details & Options", "Script", "Contact Info", "Review & Submit"];

const STATUS_STAGES = [
  "Script Review",
  "Script Approved",
  "Waiting On Customer",
  "In Progress",
  "Preview Sent",
  "Completed",
];

const STATUS_INFO = {
  "Script Review": {
    message: "Our team is reviewing your request and idea.",
    next: "We will prepare a script and contact you via WhatsApp or Email.",
    color: "#FF6B9D", bg: "#FFF0F7", border: "#F0D6E4",
  },
  "Script Approved": {
    message: "SCRIPT APPROVED — Your script has been finalised. Animation will begin soon.",
    next: "Once 50% payment is confirmed, our animators will start working on your video.",
    color: "#7C3AED", bg: "#F5F3FF", border: "#C4B5FD", alert: true,
  },
  "Waiting On Customer": {
    message: "ACTION REQUIRED — We are waiting for your response or approval.",
    next: "Please check your WhatsApp or Email and reply to our team.",
    color: "#0891B2", bg: "#ECFEFF", border: "#A5F3FC", alert: true,
  },
  "In Progress": {
    message: "ANIMATION IN PROGRESS — Our animators are currently working on your video.",
    next: "We will send you a preview before the final delivery.",
    color: "#D97706", bg: "#FFFBEB", border: "#FDE68A", alert: true,
  },
  "Preview Sent": {
    message: "PREVIEW SENT — Please check your WhatsApp or Email.",
    next: "Review the preview and approve. Final delivery follows your approval and final payment.",
    color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE", alert: true,
  },
  "Completed": {
    message: "ORDER COMPLETED — Your custom video has been delivered.",
    next: null,
    color: "#16A34A", bg: "#F0FFF4", border: "#86EFAC", success: true,
  },
};

const TESTIMONIALS = [
  { name: "Arjun", country: "India", text: "The video made my girlfriend cry. Thank you so much!", stars: 5 },
  { name: "Emily", country: "USA", text: "This was the best anniversary gift ever.", stars: 5 },
  { name: "Priya", country: "India", text: "Amazing quality! My boyfriend loved it so much. Will definitely order again!", stars: 5 },
  { name: "Carlos", country: "Mexico", text: "Super cute video for our anniversary. Highly recommend to every couple!", stars: 5 },
  { name: "Aisha", country: "UAE", text: "The team was so sweet and patient. The video was absolutely perfect!", stars: 5 },
];

const FAQS = [
  {
    q: "How long does it take to create my video?",
    a: "Our standard delivery time is 5–7 days after the script is approved. If you select Emergency Delivery, your video can be completed within 24–48 hours, depending on availability. You will also receive a preview before the final delivery.",
  },
  {
    q: "Can I change the script after submitting my order?",
    a: "Yes. Script changes are unlimited before animation begins. We will discuss the script with you and finalize it together before starting the animation. Once the animation process begins, changes may no longer be possible.",
  },
  {
    q: "Is my video private?",
    a: "Yes, your video is completely private. It will only be delivered to you through Instagram, WhatsApp, or Google Drive downloadable link. Your video will not be posted on our page. If you would like us to share it on our Instagram page @dudu_phudu, an additional fee may apply and it depends on slot availability.",
  },
  {
    q: "When do I need to make payment?",
    a: "Payment is divided into two stages: 50% payment after the script is approved, and 50% payment after the final video is delivered. This ensures you are satisfied with the script before we begin animation.",
  },
  {
    q: "Can I include my own script or story idea?",
    a: "Yes. You can either write your own script or share your idea or story. Our team will review it and convert it into a custom animated story using Bubu & Dudu characters.",
  },
  {
    q: "Can the video be in different languages?",
    a: "Currently, the primary supported languages are English and Hindi. If you want to include short lines from another language, you will need to provide a voice note for pronunciation guidance.",
  },
  {
    q: "Can I share the video publicly?",
    a: "Yes. Once your video is delivered, you are free to share it anywhere, including Instagram, WhatsApp, YouTube, and other social media platforms.",
  },
  {
    q: "What happens after I submit my request?",
    a: "Once your request is submitted: (1) Our team reviews your idea, (2) We prepare a script for your approval, (3) After approval and 50% payment, animation begins, (4) You receive a preview, (5) The final video is delivered. You can track your order status anytime using the Track Order page.",
  },
];

const EXAMPLE_REELS = [
  { url: "https://www.instagram.com/reel/DVpkbs7SnG2", id: "DVpkbs7SnG2" },
  { url: "https://www.instagram.com/reel/DVFJNdFE0wb", id: "DVFJNdFE0wb" },
  { url: "https://www.instagram.com/reel/DUvkXyBj0Vk", id: "DUvkXyBj0Vk" },
  { url: "https://www.instagram.com/reel/DQI4rH9EXiq", id: "DQI4rH9EXiq" },
  { url: "https://www.instagram.com/reel/DVgesGkj-Q9", id: "DVgesGkj-Q9" },
];

// ── HELPERS ──────────────────────────────────────────────────────
const genId = () => {
  const ts = Date.now().toString(36).toUpperCase().slice(-4);
  const rand = Math.floor(1000 + Math.random() * 9000);
  return `BD-${ts}${rand}`;
};

const getNextOrderId = async () => {
  const counterRef = doc(db, "meta", "orderCounter");
  const newLastId = await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(counterRef);
    if (!snap.exists()) throw new Error("orderCounter document missing");
    const current = snap.data().lastId;
    const next = current + 1;
    transaction.update(counterRef, { lastId: next });
    return next;
  });
  return `BD-${newLastId}`;
};
const nowISO = () => new Date().toISOString().split("T")[0];
const nowFull = () =>
  new Date().toLocaleString("en-GB", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

function deadlineFrom(delivery, customDate) {
  const now = new Date();
  const istOffset = 5.5 * 60 * 60 * 1000;
  const istNow = new Date(now.getTime() + istOffset);

  if (delivery === "Emergency (24–48 Hours)") {
    istNow.setDate(istNow.getDate() + 2);
  } else if (delivery === "Custom Date" && customDate) {
    return customDate;
  } else {
    istNow.setDate(istNow.getDate() + 7);
  }
  return istNow.toISOString().split("T")[0];
}

function daysRemaining(deadlineISO) {
  if (!deadlineISO) return 0;
  const diff = new Date(deadlineISO) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

const validateWhatsApp = (v) => {
  const cleaned = v.replace(/[\s+\-().]/g, "");
  return /^[0-9]{7,15}$/.test(cleaned);
};
const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

// ── SERVICES ─────────────────────────────────────────────────────
async function sendWhatsAppAlert(order) {
  try {
    const msg = encodeURIComponent(
      `🚨 New Bubu Dudu Order!\n\nOrder ID: ${order.id}\nCustomer: ${order.name}\nCountry: ${order.country}\nVideo: ${order.length}\nDelivery: ${order.delivery}\nLanguage: ${order.language}\nScript: ${order.scriptChoice === "write" ? "Customer wrote it" : "Needs help writing"}\nSubmitted: ${order.submittedAt}`
    );
    await fetch(
      `https://api.textmebot.com/send.php?recipient=${YOUR_WHATSAPP_NUMBER}&apikey=${TEXTMEBOT_API_KEY}&text=${msg}`,
      { method: "GET", mode: "no-cors" }
    );
  } catch (e) { console.warn("WhatsApp alert failed:", e); }
}

async function sendCustomerEmail(order) {
  try {
    if (!window.emailjs) return;
    await window.emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
      order_id: order.id,
      customer_name: order.name,
      customer_email: order.email,
      video_length: order.length,
      delivery_speed: order.delivery,
      language: order.language || "—",
      script_type: order.scriptChoice === "write" ? "Customer wrote their own script" : "Needs help writing script",
      order_date: order.submittedAt,
      whatsapp: order.whatsapp || "—",
      instagram: order.instagram || "—",
      subject: "Your Bubu Dudu Order Received 🎀",
    });
  } catch (e) { console.warn("Email send failed:", e); }
}

async function checkSpamSubmission(email) {
  if (!email) return false;
  try {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 7);
    const cutoffISO = cutoff.toISOString().split("T")[0];
    const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 5000));
    const snap = await Promise.race([
      getDocs(query(collection(db,"orders"),where("email","==",email))),
      timeout,
    ]);
    return snap.docs.some(d => (d.data().date||"") >= cutoffISO);
  } catch { return false; }
}

// ── THEME ────────────────────────────────────────────────────────
const pk = "#FF6B9D", pu = "#C084FC", cr = "#FFF8F3";
const tm = "#3D2C35", ts = "#8B6B78", bd = "#F0D6E4";
const inp = {
  width: "100%", padding: "11px 14px", borderRadius: 14,
  border: `1.5px solid ${bd}`, background: "#fff", color: tm,
  fontSize: 14, fontFamily: "'Nunito',sans-serif", outline: "none",
  boxSizing: "border-box",
};
const lbl = {
  fontSize: 12, fontWeight: 800, color: ts, marginBottom: 5,
  display: "block", letterSpacing: 0.4, textTransform: "uppercase",
};
const card = {
  background: "#fff", borderRadius: 20, padding: "20px 22px",
  border: `1.5px solid ${bd}`, marginBottom: 16,
  boxShadow: "0 2px 16px rgba(255,107,157,0.06)",
};

// ── DEFAULT FORM VALUES ──────────────────────────────────────────
const defaultFormValues = {
  country: "", orientation: "", length: "", delivery: "", customDate: "",
  language: "", scriptChoice: "", scriptText: "", occasions: [], shortIdea: "",
  activities: [], relationshipStory: "", mood: "", backgroundPref: "",
  sceneStyle: "", referenceLink: "", referenceText: "",
  yourName: "", partnerName: "",
  name: "", email: "", whatsapp: "", instagram: "",
  agreed: false, agreedPrivacy: false,
};

// ── MAIN COMPONENT ───────────────────────────────────────────────
export default function BookingApp() {

  // ── STATE ──
  const [page, setPage] = useState("book");
  const [submitted, setSubmitted] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [orderIdLoading, setOrderIdLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(() => {
    try {
      const saved = localStorage.getItem("bd_form_draft");
      if (saved) {
        const parsed = JSON.parse(saved);
        return typeof parsed.step === "number" ? parsed.step : 0;
      }
    } catch {}
    return 0;
  });

  const [form, setFormState] = useState(() => {
    try {
      const saved = localStorage.getItem("bd_form_draft");
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.form || { ...defaultFormValues };
      }
    } catch {}
    return { ...defaultFormValues };
  });

  // Track Order state
  const [trackSearch, setTrackSearch] = useState("");
  const [trackResults, setTrackResults] = useState(null);
  const [trackLoading, setTrackLoading] = useState(false);
  const [trackError, setTrackError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [trackUnsubscribe, setTrackUnsubscribe] = useState(null);

  // Edit Order state
  const [editing, setEditing] = useState(false);
  const [editForm, setEditFormState] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Rating state
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [ratingSubmitted, setRatingSubmitted] = useState(false);

  // FAQ state
  const [openFaq, setOpenFaq] = useState(null);

  const [waTouched, setWaTouched] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [blacklist, setBlacklist] = useState({ emails: [], whatsapp: [] });
  const [upsellSelected, setUpsellSelected] = useState(false);
  const [autoSearch, setAutoSearch] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search).get("order");
      return p ? p.toUpperCase() : "";
    } catch { return ""; }
  });

  // Referral tracking
  const [refName, setRefName] = useState("");
  const [refValid, setRefValid] = useState(false);

  // Draft auto-save indicator
  const [draftSaved, setDraftSaved] = useState(false);

  // Order ID copy
  const [copied, setCopied] = useState(false);

  // Online/offline
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Emergency urgency timer
  const [emergencyCountdown, setEmergencyCountdown] = useState("");

  // Emergency availability from admin settings
  const [emergencyAvailable, setEmergencyAvailable] = useState(true);

  // Flash offer from admin settings
  const [flashOffer, setFlashOffer] = useState(null);

  // Step animation ref
  const stepDotsRef = useRef([]);

  // ── HELPERS ──
  const set = (k, v) => setFormState(f => ({ ...f, [k]: v }));
  const tog = (k, v) => setFormState(f => ({
    ...f, [k]: f[k].includes(v) ? f[k].filter(x => x !== v) : [...f[k], v],
  }));

  // ── VALIDATION ──
  const whatsappError = waTouched
    ? (!form.whatsapp.trim()
        ? "WhatsApp number is required"
        : !validateWhatsApp(form.whatsapp)
        ? "Enter number with country code e.g. +919265802481"
        : "")
    : "";
  const emailError = form.email && !validateEmail(form.email)
    ? "Please enter a valid email address (e.g. name@gmail.com)" : "";

  const emergencyBlocked = (() => {
    if (form.delivery !== "Emergency (24–48 Hours)") return false;
    if (!emergencyAvailable) return true;
    const today = nowISO();
    const tom = new Date(); tom.setDate(tom.getDate() + 1);
    const tomISO = tom.toISOString().split("T")[0];
    return blockedDates.includes(today) || blockedDates.includes(tomISO);
  })();
  const canStep0 = form.country && form.orientation && form.length && form.delivery
    && (form.delivery !== "Custom Date" || form.customDate) && form.language
    && !emergencyBlocked;
  const canStep1 = form.scriptChoice && (
    form.scriptChoice === "write"
      ? form.scriptText.trim().length > 10
      : form.occasions.length > 0 && form.shortIdea.trim().length > 5
  );
  const canStep2 = form.name.trim() && validateEmail(form.email)
    && form.whatsapp.trim() && !whatsappError;
  const canSubmit = form.agreed && form.agreedPrivacy && !saving;

  // ── FORM COMPLETION % ──
  const formProgress = (() => {
    if (step === 0) {
      const fields = [form.country, form.orientation, form.length, form.delivery, form.language];
      return Math.round((fields.filter(Boolean).length / 5) * 100);
    }
    if (step === 1) {
      if (form.scriptChoice === "write") {
        const filled = [form.scriptChoice, form.scriptText.trim().length > 10 ? "y" : ""].filter(Boolean).length;
        return Math.round((filled / 2) * 100);
      }
      const filled = [form.scriptChoice, form.occasions.length > 0 ? "y" : "", form.shortIdea.trim().length > 5 ? "y" : ""].filter(Boolean).length;
      return Math.round((filled / 3) * 100);
    }
    if (step === 2) {
      const fields = [form.name.trim(), validateEmail(form.email) ? "y" : "", form.whatsapp.trim()].filter(Boolean).length;
      return Math.round((fields / 3) * 100);
    }
    return 100;
  })();

  // ── TENOR GIF SCRIPT — loaded once, never removed ──
  useEffect(() => {
    if (document.getElementById("tenor-embed-script")) return;
    const script = document.createElement("script");
    script.id = "tenor-embed-script";
    script.src = "https://tenor.com/embed.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  // ── EMAILJS SCRIPT — loaded once on mount ──
  useEffect(() => {
    const s = document.createElement("script");
    s.src = "https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js";
    s.onload = () => {
      if (window.emailjs) window.emailjs.init(EMAILJS_PUBLIC_KEY);
    };
    document.head.appendChild(s);
  }, []);

  // ── FORM DRAFT AUTO-SAVE ──
  useEffect(() => {
    if (form.name || form.email || form.country) {
      localStorage.setItem("bd_form_draft", JSON.stringify({ form, step }));
      if (step > 0) {
        setDraftSaved(true);
        const t = setTimeout(() => setDraftSaved(false), 2000);
        return () => clearTimeout(t);
      }
    }
  }, [form, step]);

  // ── SETTINGS / AVAILABILITY FETCH ──
  useEffect(() => {
    getDocs(collection(db, "settings")).then(snap =>
      snap.forEach(d => {
        if (d.id === "availability") {
          setBlockedDates(d.data().blockedDates || []);
          if (d.data().emergencyAvailable === false) setEmergencyAvailable(false);
        }
        if (d.id === "blacklist") setBlacklist({ emails: d.data().emails || [], whatsapp: d.data().whatsapp || [] });
        if (d.id === "offer") {
          const data = d.data();
          if (data.active && data.message) {
            const expired = data.expiresAt && new Date(data.expiresAt) < new Date();
            if (!expired) setFlashOffer(data);
          }
        }
      })
    ).catch(() => {});
  }, []);

  // ── TRACK LISTENER CLEANUP ──
  useEffect(() => {
    return () => {
      if (trackUnsubscribe) trackUnsubscribe();
    };
  }, [trackUnsubscribe]);

  // ── DYNAMIC PAGE TITLE ──
  useEffect(() => {
    document.title = page === "track"
      ? "Track Your Order — Bubu Dudu"
      : "Book Your Custom Video — Bubu Dudu";
  }, [page]);

  // ── AUTO-SEARCH FROM URL PARAM ──
  useEffect(() => {
    if (autoSearch) {
      setPage("track");
      setTrackSearch(autoSearch);
    }
  }, []);

  useEffect(() => {
    if (autoSearch && page === "track" && !trackLoading && !selectedOrder) {
      setTimeout(() => {
        handleTrackSearch();
        setAutoSearch("");
      }, 50);
    }
  }, [page, autoSearch]);

  // ── REFERRAL TRACKING — read ?ref= param, first-click rule ──
  useEffect(() => {
    const urlRef = new URLSearchParams(window.location.search).get('ref');
    const existing = sessionStorage.getItem('bd_ref_id');
    const refToValidate = existing || urlRef;
    if (!refToValidate) return;
    // First-click rule: only store if not already set
    if (!existing && urlRef) {
      sessionStorage.setItem('bd_ref_id', urlRef);
      localStorage.setItem('bd_ref_id', urlRef);
    }
    // Validate against Firestore
    (async () => {
      try {
        const settingsSnap = await getDoc(doc(db, "settings", "referral"));
        if (settingsSnap.exists() && settingsSnap.data().systemEnabled === false) return;
        const refSnap = await getDocs(query(collection(db, "referrals"), where("refId", "==", refToValidate), where("status", "==", "active")));
        if (!refSnap.empty) {
          const refDoc = refSnap.docs[0].data();
          setRefName(refDoc.name || refDoc.refId || refToValidate);
          setRefValid(true);
        } else {
          sessionStorage.removeItem('bd_ref_id');
          localStorage.removeItem('bd_ref_id');
        }
      } catch (e) { console.warn("Referral validation failed:", e); }
    })();
  }, []);

  // ── ONLINE/OFFLINE INDICATOR ──
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);

  // ── FORM RE-ENTRY PROTECTION ──
  useEffect(() => {
    const submittedId = sessionStorage.getItem('bd_submitted');
    if (submittedId && page === "book") {
      setPage("track");
      setTrackSearch(submittedId);
    }
  }, []); 
  // ── EMERGENCY URGENCY TIMER ──
  useEffect(() => {
    if (form.delivery !== "Emergency (24–48 Hours)") { setEmergencyCountdown(""); return; }
    const calcCountdown = () => {
      const now = new Date();
      const istOffset = 5.5 * 60 * 60 * 1000;
      const istNow = new Date(now.getTime() + istOffset);
      let target = new Date(istNow);
      target.setUTCHours(14, 30, 0, 0); // 8PM IST = 14:30 UTC
      if (istNow.getUTCHours() >= 14 && istNow.getUTCMinutes() >= 30) {
        target.setUTCDate(target.getUTCDate() + 1);
      }
      const diff = target - istNow;
      if (diff <= 0) return "";
      const h = Math.floor(diff / (1000 * 60 * 60));
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      return `${h}h ${m}m`;
    };
    setEmergencyCountdown(calcCountdown());
    const interval = setInterval(() => setEmergencyCountdown(calcCountdown()), 60000);
    return () => clearInterval(interval);
  }, [form.delivery]);

  // ── HANDLERS ──
  const handleSubmit = async () => {
    setSaving(true);
    setError("");
    try {
      const isSpam = await checkSpamSubmission(form.email);
      if (isSpam) {
        setError("You have already submitted an order in the last 7 days. Please track your order or contact us on Instagram @dudu_phudu.");
        return;
      }
      const emailNorm = (form.email || "").toLowerCase().trim();
      if (blacklist.emails.map(e => e.toLowerCase()).includes(emailNorm)) {
        setError("Unable to process. Contact us on Instagram @dudu_phudu.");
        return;
      }
      const waNorm = form.whatsapp.replace(/[^0-9]/g, "");
      if (blacklist.whatsapp.map(w => w.replace(/[^0-9]/g, "")).some(w => w === waNorm)) {
        setError("Unable to process. Contact us on Instagram @dudu_phudu.");
        return;
      }
      // Generate sequential order ID at submit time (not on page load)
      let finalOrderId;
      try {
        finalOrderId = await getNextOrderId();
      } catch (e) {
        console.warn("Sequential ID failed, using fallback:", e);
        finalOrderId = genId();
      }
      setOrderId(finalOrderId);

      const { agreed, agreedPrivacy, customDate, ...formData } = form;
      const newOrder = {
        ...formData,
        id: finalOrderId,
        date: nowISO(),
        submittedAt: nowFull(),
        deadline: deadlineFrom(form.delivery, form.customDate),
        status: "Script Review",
        pricingType: getPricingInfo(form.country)?.type || "quote",
        priority: "Normal",
        adminNotes: "",
        timeline: [{ event: "Order submitted", ts: nowFull() }],
        createdAt: serverTimestamp(),
        ...(form.delivery === "Custom Date" && form.customDate
          ? { customDate: form.customDate }
          : {}),
        ...(upsellSelected ? { addOnWallpaper: true } : {}),
        ...(() => {
          const storedRef = sessionStorage.getItem('bd_ref_id') || '';
          return storedRef ? { refId: storedRef, refName: refName || storedRef } : {};
        })(),
      };
      const writeTimeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Connection timed out. Please check your internet and try again.")), 6000)
      );
      await Promise.race([addDoc(collection(db, "orders"), newOrder), writeTimeout]);

      // Fire-and-forget — never block success screen on notification delivery
      sendWhatsAppAlert(newOrder).catch(e => console.warn("WhatsApp alert skipped:", e));
      if (form.email) {
        sendCustomerEmail(newOrder).catch(e => console.warn("Email skipped:", e));
      }

      sessionStorage.removeItem('bd_ref_id');
      sessionStorage.setItem('bd_submitted', orderId);
      localStorage.removeItem("bd_form_draft");
      setSubmitted(true);
    } catch (e) {
      console.error(e);
      setError("Something went wrong. Please try again or contact us on Instagram.");
      setTimeout(() => document.getElementById("submit-error")?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
    } finally {
      setSaving(false);
    }
  };

  const handleTrackSearch = async () => {
    const val = trackSearch.trim();
    if (!val) return;
    // Stop any existing real-time listener before new search
    if (trackUnsubscribe) { trackUnsubscribe(); setTrackUnsubscribe(null); }
    setTrackLoading(true);
    setTrackError("");
    setTrackResults(null);
    setSelectedOrder(null);
    try {
      let results = [];
      if (val.includes("@") && !val.startsWith("@")) {
        // email search
        const snap = await getDocs(query(collection(db, "orders"), where("email", "==", val)));
        snap.forEach(d => results.push({ docId: d.id, ...d.data() }));
      } else if (val.toUpperCase().startsWith("BD-")) {
        // Order ID search
        const snap = await getDocs(query(collection(db, "orders"), where("id", "==", val.toUpperCase())));
        snap.forEach(d => results.push({ docId: d.id, ...d.data() }));
      } else {
        // WhatsApp number search
        const snap = await getDocs(query(collection(db, "orders"), where("whatsapp", "==", val)));
        snap.forEach(d => results.push({ docId: d.id, ...d.data() }));
        if (results.length === 0) {
          // Also try as Order ID without prefix
          const snap2 = await getDocs(query(collection(db, "orders"), where("id", "==", val.toUpperCase())));
          snap2.forEach(d => results.push({ docId: d.id, ...d.data() }));
        }
      }
      if (results.length === 0) {
        setTrackError("No orders found. Please check your email, WhatsApp number, or Order ID.");
      } else if (results.length === 1) {
        // Set up real-time listener for single result
        const foundOrder = results[0];
        const unsubFn = onSnapshot(
          query(collection(db, "orders"), where("id", "==", foundOrder.id)),
          (snap) => {
            if (!snap.empty) {
              const updated = { docId: snap.docs[0].id, ...snap.docs[0].data() };
              setSelectedOrder(updated);
            } else {
              setSelectedOrder(null);
              setTrackError("This order no longer exists. Please contact us on Instagram @dudu_phudu.");
            }
          },
          (error) => {
            console.warn("Track listener error:", error);
          }
        );
        setTrackUnsubscribe(() => unsubFn);
      } else {
        const sorted = results.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
        setTrackResults(sorted);

        // Set up a listener for each found order so status updates reflect in real time
        const unsubFns = sorted.map(foundOrder => {
          return onSnapshot(
            query(collection(db, "orders"), where("id", "==", foundOrder.id)),
            (snap) => {
              if (!snap.empty) {
                const updated = { docId: snap.docs[0].id, ...snap.docs[0].data() };
                setTrackResults(prev =>
                  prev ? prev.map(o => o.id === updated.id ? updated : o) : prev
                );
              }
            },
            (error) => { console.warn("Track listener error:", error); }
          );
        });

        // Store a combined unsubscribe
        setTrackUnsubscribe(() => () => unsubFns.forEach(fn => fn()));
      }
    } catch (e) {
      setTrackError("Unable to search. Please try again.");
    }
    setTrackLoading(false);
  };

  const handleEditSave = async () => {
    if (!selectedOrder) return;
    setEditSaving(true);
    try {
      const updates = {
        yourName: editForm.yourName || "",
        partnerName: editForm.partnerName || "",
        sceneStyle: editForm.sceneStyle || "",
        referenceLink: editForm.referenceLink || "",
        referenceText: editForm.referenceText || "",
        scriptText: editForm.scriptText || "",
        shortIdea: editForm.shortIdea || "",
      };
      await updateDoc(doc(db, "orders", selectedOrder.docId), {
        ...updates,
        customerEditedAt: new Date().toISOString(),
        customerEditNote: "Customer updated order details",
      });
      setSelectedOrder({ ...selectedOrder, ...updates });
      setEditing(false);
    } catch (e) {
      console.warn("Edit save failed:", e);
    }
    setEditSaving(false);
  };

  const handleRatingSubmit = async () => {
    if (!selectedOrder || !rating) return;
    try {
      await updateDoc(doc(db, "orders", selectedOrder.docId), {
        rating, review: reviewText, ratedAt: serverTimestamp(),
      });
      setRatingSubmitted(true);
      setSelectedOrder({ ...selectedOrder, rating, review: reviewText });
    } catch (e) { console.warn("Rating save failed"); }
  };

  // ── UI COMPONENTS ──────────────────────────────────────────────

  const Tag = ({ label, active, onClick }) => (
    <button onClick={onClick} style={{
      padding: "7px 14px", borderRadius: 30, fontSize: 12,
      border: `1.5px solid ${active ? pk : bd}`,
      background: active ? `linear-gradient(135deg,${pk},${pu})` : "#fff",
      color: active ? "#fff" : ts, cursor: "pointer",
      fontFamily: "'Nunito',sans-serif", fontWeight: active ? 700 : 500,
      margin: "3px 3px 3px 0", transition: "all 0.15s",
    }}>{label}</button>
  );

  const Btn = ({ children, onClick, disabled, secondary, small, full }) => (
    <button onClick={onClick} disabled={disabled || saving} style={{
      padding: small ? "9px 18px" : "13px 28px",
      width: full ? "100%" : "auto",
      borderRadius: 50, fontSize: small ? 13 : 14, fontWeight: 800,
      background: disabled ? "#E8D5DF" : secondary ? "#fff" : `linear-gradient(135deg,${pk},${pu})`,
      color: disabled ? "#C0A8B5" : secondary ? pk : "#fff",
      border: secondary ? `2px solid ${pk}` : "none",
      cursor: disabled ? "not-allowed" : "pointer",
      fontFamily: "'Nunito',sans-serif",
      boxShadow: disabled ? "none" : "0 4px 18px rgba(255,107,157,0.3)",
      transition: "all 0.2s",
    }}>{children}</button>
  );

  const StepDot = ({ i }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div ref={el => stepDotsRef.current[i] = el} style={{
        width: 28, height: 28, borderRadius: "50%", display: "flex",
        alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800,
        background: i < step ? `linear-gradient(135deg,${pk},${pu})` : i === step ? pk : bd,
        color: i <= step ? "#fff" : ts,
        boxShadow: i === step ? "0 2px 10px rgba(255,107,157,0.4)" : "none",
        transition: "all 0.3s", flexShrink: 0,
      }}>
        {i < step ? "✓" : i + 1}
      </div>
      {i < STEPS.length - 1 && (
        <div style={{ width: 24, height: 2, background: i < step ? pk : bd, borderRadius: 2 }} />
      )}
    </div>
  );

  const animateStepComplete = useCallback((currentStep) => {
    const dot = stepDotsRef.current[currentStep];
    if (dot) {
      dot.style.transform = "scale(1.3)";
      dot.style.background = `linear-gradient(135deg,${pk},${pu})`;
      dot.style.color = "#fff";
      setTimeout(() => {
        dot.style.transform = "scale(1)";
        setStep(currentStep + 1);
      }, 200);
    } else {
      setStep(currentStep + 1);
    }
  }, [pk, pu]); 
  const HeaderBar = () => (
    <div style={{
      background: "#fff", borderBottom: `1px solid ${bd}`,
      padding: "0 16px", height: 58, display: "flex",
      alignItems: "center", justifyContent: "space-between",
      position: "sticky", top: 0, zIndex: 50,
      boxShadow: "0 2px 8px rgba(255,107,157,0.06)",
    }}>
      {/* Tenor GIF — iframe is stable across page switches */}
      <iframe
        src="https://tenor.com/embed/26166202"
        width="50"
        height="52"
        frameBorder="0"
        allowFullScreen
        style={{ border: "none", flexShrink: 0, borderRadius: 8, display: "block" }}
        title="Bubu Dudu Hug"
      />
      {/* Nav Pills */}
      <div style={{ display: "flex", gap: 8 }}>
        {[
          { id: "book", label: "📹 Book Video" },
          { id: "track", label: "📍 Track Order" },
        ].map(({ id, label }) => (
          <button key={id} onClick={() => setPage(id)} style={{
            padding: "8px 14px", borderRadius: 50, fontSize: 12, fontWeight: 800,
            background: page === id ? `linear-gradient(135deg,${pk},${pu})` : "#FFF0F7",
            color: page === id ? "#fff" : pk,
            border: `1.5px solid ${page === id ? "transparent" : bd}`,
            cursor: "pointer", fontFamily: "'Nunito',sans-serif",
            boxShadow: page === id ? "0 2px 12px rgba(255,107,157,0.3)" : "none",
            transition: "all 0.2s",
          }}>{label}</button>
        ))}
      </div>
    </div>
  );

  const PricingSection = () => {
    if (form.country !== "India") return null;
    return (
      <div style={{
        background: "linear-gradient(135deg,#FFF0F7,#F3E8FF)", borderRadius: 14,
        padding: "16px 18px", marginTop: 4, marginBottom: 16, border: `1.5px solid ${bd}`,
      }}>
        <div style={{ fontSize: 11, fontWeight: 900, color: pk, letterSpacing: 0.5, marginBottom: 10 }}>
          💰 VIDEO PRICE
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          {[
            { label: "Short Video (~40 sec)", price: "₹1300", match: "40" },
            { label: "Long Video (~2 min)", price: "₹3500", match: "2 min" },
          ].map(({ label, price, match }) => (
            <div key={match} style={{
              flex: 1, background: "#fff", borderRadius: 12, padding: "12px 14px",
              border: `1.5px solid ${form.length && form.length.includes(match) ? pk : bd}`,
            }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: tm }}>{label}</div>
              <div style={{ fontFamily: "'Fredoka One',cursive", fontSize: 22, color: pk, marginTop: 2 }}>{price}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11.5, color: ts, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Note: Final price may increase depending on:</div>
          {["Script complexity", "Number of scenes", "Video duration"].map(pt => (
            <div key={pt} style={{ display: "flex", gap: 6, marginBottom: 3, alignItems: "center" }}>
              <span style={{ color: pk, fontWeight: 900 }}>•</span>{pt}
            </div>
          ))}
          <div style={{
            marginTop: 10, background: "#FFF8DC", borderRadius: 8,
            padding: "8px 12px", border: "1px solid #FFD700",
            fontSize: 11.5, fontWeight: 700, color: "#9A7700",
          }}>
            ⚠️ This is an estimated price only. Final price will be confirmed after script review.
          </div>
        </div>
      </div>
    );
  };

  const LazyReel = ({ reelId }) => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
      const el = ref.current;
      if (!el) return;
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
      }, { rootMargin: '200px' });
      observer.observe(el);
      return () => observer.disconnect();
    }, []);
    return (
      <div ref={ref} style={{
        minWidth: 280, height: 400, flexShrink: 0, borderRadius: 18,
        overflow: "hidden", border: `1.5px solid ${bd}`,
        boxShadow: "0 2px 12px rgba(255,107,157,0.09)",
        background: "#fff",
      }}>
        {visible ? (
          <iframe
            src={`https://www.instagram.com/reel/${reelId}/embed/`}
            width="280" height="400" frameBorder="0" scrolling="no"
            allowTransparency
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            style={{ display: "block", border: "none" }}
            title={`Bubu Dudu reel ${reelId}`}
          />
        ) : (
          <div style={{
            width: "100%", height: "100%", display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", background: "#FFF0F7",
          }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🐻‍❄️💕</div>
            <div style={{ fontSize: 12, color: ts, fontWeight: 700 }}>Loading reel...</div>
          </div>
        )}
      </div>
    );
  };

  const ExampleReelsSection = () => (
    <div style={{ marginTop: 32 }}>
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <div style={{
          fontFamily: "'Fredoka One',cursive", fontSize: 22, color: pk, marginBottom: 4,
        }}>✨ See Example Custom Videos</div>
        <p style={{ color: ts, fontSize: 13, margin: 0, fontWeight: 700 }}>
          Loved by 1000+ couples ❤️
        </p>
      </div>
      <div style={{ display: "flex", gap: 14, overflowX: "auto", paddingBottom: 10, scrollbarWidth: "none" }}>
        {EXAMPLE_REELS.map((reel) => <LazyReel key={reel.id} reelId={reel.id} />)}
      </div>
    </div>
  );

  const TestimonialsSection = () => (
    <div style={{ marginTop: 32 }}>
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <div style={{ fontFamily: "'Fredoka One',cursive", fontSize: 22, color: pk }}>
          ❤️ What Our Customers Say
        </div>
      </div>
      <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8, scrollbarWidth: "none" }}>
        {TESTIMONIALS.map((t, i) => (
          <div key={i} style={{
            minWidth: 240, background: "#fff", borderRadius: 16,
            padding: "16px 18px", border: `1.5px solid ${bd}`, flexShrink: 0,
            boxShadow: "0 2px 12px rgba(255,107,157,0.07)",
          }}>
            <div style={{ color: "#FFB347", fontSize: 15, marginBottom: 8, letterSpacing: 2 }}>
              {"⭐".repeat(t.stars)}
            </div>
            <div style={{ fontSize: 13, color: tm, lineHeight: 1.65, fontStyle: "italic", marginBottom: 10 }}>
              "{t.text}"
            </div>
            <div style={{ fontSize: 11, fontWeight: 800, color: ts }}>
              — {t.name}, {t.country}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const FAQSection = () => (
    <div style={{ marginTop: 32 }}>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{ fontFamily: "'Fredoka One',cursive", fontSize: 22, color: pk, marginBottom: 4 }}>
          💬 Frequently Asked Questions
        </div>
        <p style={{ color: ts, fontSize: 13, margin: 0, fontWeight: 600 }}>
          Everything you need to know before ordering your custom Bubu & Dudu video.
        </p>
      </div>
      {FAQS.map((faq, i) => (
        <div key={i} style={{ ...card, marginBottom: 8, padding: 0, overflow: "hidden" }}>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setOpenFaq(openFaq === i ? null : i)}
            style={{
              width: "100%", background: "none", border: "none",
              padding: "14px 18px", display: "flex", justifyContent: "space-between",
              alignItems: "center", cursor: "pointer",
              fontFamily: "'Nunito',sans-serif", gap: 12,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 800, color: tm, textAlign: "left", lineHeight: 1.4 }}>
              {faq.q}
            </span>
            <span style={{
              fontSize: 22, color: pk, flexShrink: 0,
              transition: "transform 0.2s",
              transform: openFaq === i ? "rotate(45deg)" : "none",
              display: "inline-block",
            }}>+</span>
          </button>
          {openFaq === i && (
            <div style={{
              padding: "12px 18px 16px", fontSize: 13, color: ts,
              lineHeight: 1.8, borderTop: `1px solid ${bd}`, background: "#FFFAFC",
            }}>
              {faq.a}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const ProgressBar = ({ status }) => {
    const idx = STATUS_STAGES.indexOf(status);
    return (
      <div>
        {STATUS_STAGES.map((s, i) => (
          <div key={s} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div style={{
              width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
              background: i < idx ? `linear-gradient(135deg,${pk},${pu})` : i === idx ? pk : "#E8D5DF",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, color: i <= idx ? "#fff" : ts, fontWeight: 800,
            }}>
              {i < idx ? "✓" : i + 1}
            </div>
            <div style={{
              fontSize: 13, fontWeight: i === idx ? 900 : 600,
              color: i === idx ? pk : i < idx ? tm : ts,
            }}>
              {s}
              {i === idx && (
                <span style={{
                  marginLeft: 8, fontSize: 10, background: pk,
                  color: "#fff", borderRadius: 10, padding: "2px 7px", fontWeight: 800,
                }}>CURRENT</span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const OrderDetailsView = ({ order }) => {
    const info = STATUS_INFO[order.status] ?? STATUS_INFO["Script Review"];
    const days = daysRemaining(order.deadline);
    const canEdit = order.status === "Script Review";

    return (
      <div>
        <button
          onClick={() => { setSelectedOrder(null); setEditing(false); }}
          style={{
            background: "none", border: "none", color: pk, fontWeight: 800,
            fontSize: 13, cursor: "pointer", marginBottom: 16,
            fontFamily: "'Nunito',sans-serif", padding: 0, display: "flex",
            alignItems: "center", gap: 4,
          }}
        >← Back to Orders</button>

        {/* Order Header */}
        <div style={{ ...card, background: "linear-gradient(135deg,#FFF0F7,#F3E8FF)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 900, color: pk, letterSpacing: 0.5 }}>
              📋 ORDER DETAILS
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#16A34A" }} />
              <span style={{ fontSize: 11, color: "#16A34A", fontWeight: 700 }}>Updates live</span>
            </div>
          </div>
          {[
            ["Order ID", order.id],
            ["Customer", order.name],
            ["Submitted", order.submittedAt || order.date],
            ["Video Length", order.length],
            ["Delivery Speed", order.delivery],
            ["Language", order.language || "—"],
            ["Estimated Delivery", order.deadline
              ? `${new Date(order.deadline + 'T23:59:59').toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} · ${days} day${days !== 1 ? "s" : ""} remaining`
              : "—"],
          ].map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 7, gap: 12 }}>
              <span style={{ color: ts, fontWeight: 600, flexShrink: 0 }}>{k}</span>
              <span style={{ color: tm, fontWeight: 700, textAlign: "right", flex: 1 }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Current Status */}
        <div style={{ ...card, background: info.bg, border: `1.5px solid ${info.border}` }}>
          <div style={{ fontSize: 11, fontWeight: 900, color: info.color, letterSpacing: 0.5, marginBottom: 10 }}>
            CURRENT STATUS — {order.status.toUpperCase()}
          </div>
          <div style={{ fontSize: 13, color: tm, fontWeight: 700, lineHeight: 1.6 }}>
            {info.message}
          </div>
          {info.next && (
            <div style={{
              marginTop: 12, padding: "10px 14px",
              background: "rgba(255,255,255,0.7)", borderRadius: 10,
              border: `1px dashed ${bd}`,
            }}>
              <div style={{ fontSize: 10, fontWeight: 900, color: ts, letterSpacing: 0.3, marginBottom: 4 }}>
                NEXT STEP
              </div>
              <div style={{ fontSize: 12.5, color: ts, lineHeight: 1.65 }}>{info.next}</div>
            </div>
          )}
        </div>

        {/* Delivery Link — shown whenever admin has added one */}
        {order.deliveryLink && (
          <div style={{
            marginTop: 14, padding: "12px 16px",
            background: "rgba(255,255,255,0.8)", borderRadius: 10,
            border: "1.5px solid #86EFAC",
          }}>
            <div style={{ fontSize: 10, fontWeight: 900, color: "#16A34A", letterSpacing: 0.3, marginBottom: 6 }}>
              📦 YOUR VIDEO IS READY
            </div>
            <a
              href={order.deliveryLink}
              target="_blank"
              rel="noreferrer"
              style={{
                display: "block", fontSize: 13, color: "#2563EB",
                fontWeight: 700, wordBreak: "break-all", textDecoration: "underline",
              }}
            >
              {order.deliveryLink}
            </a>
            {order.deliveredAt && (
              <div style={{ fontSize: 11, color: "#6B7280", marginTop: 6 }}>
                Delivered: {order.deliveredAt}
              </div>
            )}
          </div>
        )}

        {/* Progress Bar */}
        <div style={card}>
          <div style={{ fontSize: 11, fontWeight: 900, color: pk, letterSpacing: 0.5, marginBottom: 16 }}>
            📊 ORDER PROGRESS
          </div>
          <ProgressBar status={order.status} />
        </div>

        {/* Timeline */}
        {order.timeline && order.timeline.length > 0 && (
          <div style={card}>
            <div style={{ fontSize: 11, fontWeight: 900, color: pk, letterSpacing: 0.5, marginBottom: 14 }}>
              📅 ORDER TIMELINE
            </div>
            {(order.timeline || [])
              .filter(entry => {
                const e = (entry.event || "").toLowerCase();
                return !e.startsWith("priority →") &&
                       !e.startsWith("note added") &&
                       !e.startsWith("archived:") &&
                       !e.startsWith("restored");
              })
              .map((entry, i) => {
                let label = entry.event || "";
                if (label.startsWith("Status →")) {
                  label = "📋 " + label.replace("Status → ", "Status updated to: ");
                } else if (label === "Order submitted") {
                  label = "✅ Order received — thank you!";
                } else if (label.startsWith("Duplicated from")) {
                  label = "📋 Order created";
                }
                return (
                  <div key={i} style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "flex-start" }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: pk, marginTop: 5, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 13, color: tm, fontWeight: 600 }}>{label}</div>
                      <div style={{ fontSize: 11, color: ts, marginTop: 2 }}>{entry.ts}</div>
                    </div>
                  </div>
                );
              })
            }
          </div>
        )}

        {/* Edit Order */}
        {canEdit && !editing && (
          <button
            onClick={() => {
              setEditFormState({
                yourName: order.yourName || "", partnerName: order.partnerName || "",
                sceneStyle: order.sceneStyle || "", referenceLink: order.referenceLink || "",
                referenceText: order.referenceText || "", scriptText: order.scriptText || "",
                shortIdea: order.shortIdea || "",
              });
              setEditing(true);
            }}
            style={{
              width: "100%", padding: "12px", borderRadius: 14,
              border: `1.5px dashed ${pk}`, background: "#FFF0F7",
              color: pk, fontWeight: 800, fontSize: 13, cursor: "pointer",
              fontFamily: "'Nunito',sans-serif", marginBottom: 16,
            }}
          >
            ✏️ Edit Order (available until Script Writing begins)
          </button>
        )}

        {!canEdit && order.status !== "Script Review" && (
          <div style={{
            textAlign: "center", padding: "10px 14px", borderRadius: 12,
            background: "#F5F5F5", border: "1px dashed #ccc", marginBottom: 16,
            fontSize: 12, color: ts, fontWeight: 600,
          }}>
            ✏️ Editing is no longer available — animation has started.
          </div>
        )}

        {editing && (
          <div style={card}>
            <div style={{ fontSize: 11, fontWeight: 900, color: pk, letterSpacing: 0.5, marginBottom: 14 }}>
              ✏️ EDIT ORDER
            </div>
            {[
              ["yourName", "Your Name"],
              ["partnerName", "Partner Name"],
              ["referenceLink", "Google Docs Reference Link"],
            ].map(([k, label]) => (
              <div key={k} style={{ marginBottom: 12 }}>
                <label style={lbl}>{label}</label>
                <input
                  value={editForm[k] || ""}
                  onChange={e => setEditFormState(f => ({ ...f, [k]: e.target.value }))}
                  style={inp}
                />
              </div>
            ))}
            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Scene Style</label>
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                {SCENE_STYLES.map(s => (
                  <button key={s} onClick={() => setEditFormState(f => ({ ...f, sceneStyle: s }))} style={{
                    padding: "7px 14px", borderRadius: 30, fontSize: 12,
                    border: `1.5px solid ${editForm.sceneStyle === s ? pk : bd}`,
                    background: editForm.sceneStyle === s ? `linear-gradient(135deg,${pk},${pu})` : "#fff",
                    color: editForm.sceneStyle === s ? "#fff" : ts,
                    cursor: "pointer", fontFamily: "'Nunito',sans-serif", margin: "3px 3px 3px 0",
                  }}>{s}</button>
                ))}
              </div>
            </div>
            {order.scriptChoice === "write" && (
              <div style={{ marginBottom: 12 }}>
                <label style={lbl}>Script</label>
                <textarea
                  value={editForm.scriptText || ""}
                  onChange={e => setEditFormState(f => ({ ...f, scriptText: e.target.value }))}
                  rows={6} style={{ ...inp, resize: "vertical" }}
                />
              </div>
            )}
            {order.scriptChoice === "help" && (
              <div style={{ marginBottom: 12 }}>
                <label style={lbl}>Short Idea</label>
                <textarea
                  value={editForm.shortIdea || ""}
                  onChange={e => setEditFormState(f => ({ ...f, shortIdea: e.target.value }))}
                  rows={3} style={{ ...inp, resize: "vertical" }}
                />
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Reference Notes</label>
              <textarea
                value={editForm.referenceText || ""}
                onChange={e => setEditFormState(f => ({ ...f, referenceText: e.target.value }))}
                rows={3} style={{ ...inp, resize: "vertical" }}
              />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Btn secondary onClick={() => setEditing(false)} small>Cancel</Btn>
              <Btn onClick={handleEditSave} disabled={editSaving} small>
                {editSaving ? "Saving…" : "Save Changes"}
              </Btn>
            </div>
          </div>
        )}

        {/* Rating (Completed orders) */}
        {order.status === "Completed" && !ratingSubmitted && !order.rating && (
          <div style={card}>
            <div style={{ fontSize: 11, fontWeight: 900, color: pk, letterSpacing: 0.5, marginBottom: 12 }}>
              ⭐ RATE YOUR EXPERIENCE
            </div>
            <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} onClick={() => setRating(n)} style={{
                  fontSize: 28, background: "none", border: "none",
                  cursor: "pointer", opacity: n <= rating ? 1 : 0.25,
                  transition: "opacity 0.15s", padding: 0,
                }}>⭐</button>
              ))}
            </div>
            <textarea
              value={reviewText}
              onChange={e => setReviewText(e.target.value)}
              placeholder="Share your experience (optional)…"
              rows={3}
              style={{ ...inp, resize: "none", marginBottom: 12 }}
            />
            <Btn onClick={handleRatingSubmit} disabled={!rating} small>
              Submit Review ✨
            </Btn>
          </div>
        )}
        {(ratingSubmitted || order.rating) && (
          <div style={{ ...card, background: "#F0FFF4", border: "1.5px solid #86EFAC", textAlign: "center" }}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>🎉</div>
            <div style={{ fontWeight: 800, color: "#16A34A", fontSize: 14 }}>
              Thank you for your review!
            </div>
          </div>
        )}

        {/* Contact Support */}
        <div style={{ ...card, textAlign: "center", background: "linear-gradient(135deg,#FFF0F7,#F3E8FF)" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: ts, marginBottom: 8 }}>
            Need help with your order?
          </div>
          <a href="https://instagram.com/dudu_phudu" target="_blank" rel="noreferrer"
            style={{ color: pk, fontWeight: 800, fontSize: 14, textDecoration: "none" }}>
            📸 Message us on Instagram @dudu_phudu
          </a>
        </div>
      </div>
    );
  };

  // ── SUCCESS SCREEN ─────────────────────────────────────────────
  if (submitted) {
    const dl = deadlineFrom(form.delivery, form.customDate);
    const days = daysRemaining(dl);
    const isEmergency = form.delivery.includes("Emergency");
    return (
      <div style={{ minHeight: "100vh", background: cr, fontFamily: "'Nunito',sans-serif" }}>
        <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&family=Fredoka+One&display=swap" rel="stylesheet" />
        <HeaderBar />
        <div style={{ maxWidth: 520, margin: "0 auto", padding: "24px 16px 60px", textAlign: "center" }}>
          <div style={{ ...card, padding: "40px 32px" }}>
              {/* Pricing Card */}
            <div style={{ fontSize: 56, marginBottom: 12 }}>🎉</div>
            <div style={{ fontFamily: "'Fredoka One',cursive", fontSize: 26, color: pk, marginBottom: 8 }}>
              Request Received!
            </div>
            <p style={{ color: ts, fontSize: 14, lineHeight: 1.7, margin: "0 0 8px" }}>
              Your custom video request is confirmed! We'll reach out shortly. 💌
            </p>
            {form.email && (
              <p style={{ color: ts, fontSize: 13, marginBottom: 20 }}>
                A confirmation email was sent to <strong>{form.email}</strong>
              </p>
            )}

            {/* Order ID */}
            <div style={{
              background: "linear-gradient(135deg,#FFF0F7,#F3E8FF)",
              border: `1.5px dashed ${pk}`, borderRadius: 16, padding: "16px 20px", marginBottom: 16,
            }}>
              <div style={{ fontSize: 11, color: ts, fontWeight: 800, letterSpacing: 0.5, marginBottom: 4 }}>
                YOUR ORDER ID — SAVE THIS
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <div style={{ fontFamily: "'Fredoka One',cursive", fontSize: 28, color: pk, letterSpacing: 1 }}>
                  {orderId || "Generating..."}
                </div>
                {orderId && (
                  <button onClick={() => {
                    navigator.clipboard.writeText(orderId).then(() => {
                      setCopied(true); setTimeout(() => setCopied(false), 2000);
                    });
                  }} style={{
                    background: copied ? "#F0FDF4" : "#fff", border: `1px solid ${copied ? "#86EFAC" : bd}`,
                    borderRadius: 8, padding: "4px 10px", fontSize: 11, fontWeight: 800,
                    color: copied ? "#16A34A" : ts, cursor: "pointer", fontFamily: "'Nunito',sans-serif",
                    transition: "all 0.2s",
                  }}>{copied ? "✅ Copied!" : "📋 Copy"}</button>
                )}
              </div>
              <div style={{ fontSize: 11, color: ts, marginTop: 4 }}>
                Use this to track your order status
              </div>
            </div>

            {/* WhatsApp Share */}
            {orderId && (
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`My Bubu Dudu order ID: ${orderId}\nTrack here: https://bubududucustom.com/?order=${orderId}`)}`}
                target="_blank" rel="noreferrer"
                style={{
                  display: "block", textAlign: "center", padding: "10px", borderRadius: 50,
                  background: "#25D366", color: "#fff", fontWeight: 800, fontSize: 13,
                  textDecoration: "none", marginBottom: 16, fontFamily: "'Nunito',sans-serif",
                }}
              >💬 Share Order ID on WhatsApp</a>
            )}

            {/* Order Details Card */}
            <div style={{
              background: "#fff", borderRadius: 16, padding: "16px 20px", marginBottom: 16,
              border: `1.5px solid ${bd}`, textAlign: "left",
            }}>
              <div style={{ fontSize: 11, fontWeight: 900, color: pk, letterSpacing: 0.5, marginBottom: 12 }}>
                📋 ORDER DETAILS
              </div>
              {[
                ["Name", form.name],
                ["Email", form.email],
                ["WhatsApp", form.whatsapp],
                ["Country", form.country],
                ["Video Length", form.length],
                ["Delivery", form.delivery],
                ["Language", form.language],
              ].filter(([,v]) => v).map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 7, gap: 12 }}>
                  <span style={{ color: ts, fontWeight: 600, flexShrink: 0 }}>{k}</span>
                  <span style={{ color: tm, fontWeight: 700, textAlign: "right", flex: 1 }}>{v}</span>
                </div>
              ))}
            </div>

            {/* Delivery Countdown */}
            <div style={{
              background: isEmergency ? "#FFF3E0" : "#F0FFF4",
              border: `1.5px solid ${isEmergency ? "#FFB74D" : "#86EFAC"}`,
              borderRadius: 14, padding: "12px 18px", marginBottom: 20,
            }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: isEmergency ? "#E07B00" : "#16A34A" }}>
                ⏱ Estimated delivery: {isEmergency
                  ? "24–48 hours"
                  : `${days} day${days !== 1 ? "s" : ""} remaining`}
              </div>
              <div style={{ fontSize: 13, color: tm, fontWeight: 600, marginTop: 4 }}>
                {!isEmergency && dl ? new Date(dl + 'T23:59:59').toLocaleDateString(
                  undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
                ) + " (your local time)" : isEmergency ? "Emergency delivery requested" : ""}
              </div>
              {!isEmergency && dl && (
                <div style={{ fontSize: 11, color: ts, marginTop: 2 }}>IST target: {dl}</div>
              )}
            </div>

            {/* Track Order Button */}
            <button
              onClick={() => { setPage("track"); setTrackSearch(orderId); }}
              style={{
                width: "100%", padding: "13px", borderRadius: 50,
                background: `linear-gradient(135deg,${pk},${pu})`, color: "#fff",
                fontWeight: 800, fontSize: 14, border: "none", cursor: "pointer",
                fontFamily: "'Nunito',sans-serif",
                boxShadow: "0 4px 18px rgba(255,107,157,0.3)", marginBottom: 12,
              }}
            >
              📍 Track My Order
            </button>

            {/* Book Another Video Button */}
            <button
              onClick={() => {
                setSubmitted(false);
                setFormState({ ...defaultFormValues });
                setStep(0);
                setOrderId("");
                localStorage.removeItem("bd_form_draft");
              }}
              style={{
                width: "100%", padding: "13px", borderRadius: 50,
                background: "#fff", color: pk, border: `1.5px solid ${pk}`,
                fontWeight: 800, fontSize: 14, cursor: "pointer",
                fontFamily: "'Nunito',sans-serif", marginBottom: 20,
              }}
            >
              🎀 Book Another Video
            </button>

            {/* What Happens Next */}
            <div style={{ ...card, textAlign: "left", background: "#FFF0F7", border: `1.5px solid ${bd}`, marginBottom: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: pk, marginBottom: 12, letterSpacing: 0.5 }}>
                WHAT HAPPENS NEXT
              </div>
              {[
                ["🔍", "Script Review", "We review your request & idea"],
                ["✍️", "Script Approval", "We share the script — you approve it"],
                ["🎨", "Animation", "We bring your story to life"],
                ["👀", "Preview", "You get a preview before final delivery"],
                ["🎀", "Completed", "Your video is ready to share!"],
              ].map(([ic, t, d]) => (
                <div key={t} style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 15, flexShrink: 0 }}>{ic}</span>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 13, color: tm }}>{t}</div>
                    <div style={{ fontSize: 12, color: ts }}>{d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 14, fontSize: 13, color: ts, fontWeight: 600 }}>
            Questions?{" "}
            <a href="https://instagram.com/dudu_phudu" style={{ color: pk, fontWeight: 800 }}>
              @dudu_phudu
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── MAIN RENDER ────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: cr, fontFamily: "'Nunito',sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&family=Fredoka+One&display=swap" rel="stylesheet" />

      <HeaderBar />

      {/* ══════════ BOOKING PAGE ══════════ */}
      {page === "book" && (
        <div style={{ maxWidth: 520, margin: "0 auto", padding: "24px 16px 60px" }}>

          {/* Title */}
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={{ fontSize: 36, marginBottom: 4 }}>✨</div>
            <h1 style={{
              fontFamily: "'Fredoka One',cursive", fontSize: 24, margin: "0 0 6px",
              background: `linear-gradient(135deg,${pk},${pu})`,
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>Book Your Custom Video</h1>
            <p style={{ color: ts, fontSize: 13, margin: 0, fontWeight: 600 }}>
              Fill in the details and we'll create something magical just for you
            </p>
          </div>

          {/* Flash Offer Banner */}
          {flashOffer && (
            <div style={{
              background: "linear-gradient(135deg,#FFF0F7,#FEF3C7)",
              border: `1.5px solid #FFD700`, borderRadius: 14, padding: "10px 16px",
              marginBottom: 14, textAlign: "center",
            }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: tm }}>{flashOffer.message}</div>
              {flashOffer.expiresAt && (
                <div style={{ fontSize: 11, color: ts, marginTop: 2 }}>Expires: {flashOffer.expiresAt}</div>
              )}
            </div>
          )}

          {/* Referral badge removed — ref tracking is silent */}

          {/* Step Indicator */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8, alignItems: "center" }}>
            {STEPS.map((_, i) => <StepDot key={i} i={i} />)}
          </div>

          {/* Draft saved indicator */}
          <div style={{
            textAlign: "center", marginBottom: 4, height: 18,
            opacity: draftSaved ? 1 : 0, transition: "opacity 0.3s",
          }}>
            <span style={{ fontSize: 11, color: ts, fontWeight: 600 }}>💾 Draft saved</span>
          </div>

          {/* Form completion % */}
          <div style={{ marginBottom: 20, padding: "0 4px" }}>
            <div style={{ height: 4, borderRadius: 2, background: bd, overflow: "hidden" }}>
              <div style={{
                height: "100%", borderRadius: 2, transition: "width 0.3s",
                background: `linear-gradient(90deg,${pk},${pu})`,
                width: `${formProgress}%`,
              }} />
            </div>
            <div style={{ textAlign: "right", fontSize: 10, color: ts, fontWeight: 700, marginTop: 2 }}>
              {formProgress}% filled
            </div>
          </div>

          {/* Clear saved draft */}
          {localStorage.getItem("bd_form_draft") && (
            <div style={{ textAlign: "center", marginBottom: 12 }}>
              <button onClick={() => {
                localStorage.removeItem("bd_form_draft");
                setFormState({ ...defaultFormValues });
                setStep(0);
                setOrderId("");
              }} style={{
                background: "none", border: "none", color: ts, fontSize: 12,
                fontWeight: 700, cursor: "pointer", textDecoration: "underline",
                fontFamily: "'Nunito',sans-serif",
              }}>← Start fresh</button>
            </div>
          )}

          {/* ─── STEP 0 · DETAILS ─── */}
          {step === 0 && (
            <div>
              <div style={card}>
                <div style={{ fontSize: 12, fontWeight: 900, color: pk, marginBottom: 14, letterSpacing: 0.5 }}>
                  🌍 STEP 1 · DETAILS & OPTIONS
                </div>

                {/* Country */}
                <div style={{ marginBottom: 16 }}>
                  <label style={lbl}>Your Country</label>
                  <div style={{ position: "relative" }}>
                    <select
                      value={form.country}
                      onChange={e => set("country", e.target.value)}
                      style={{ ...inp, cursor: "pointer", appearance: "none" }}
                    >
                      <option value="">Select your country…</option>
                      {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: ts }}>▾</span>
                  </div>
                </div>

                {/* Orientation */}
                <div style={{ marginBottom: 16 }}>
                  <label style={lbl}>Video Orientation</label>
                  <div style={{ display: "flex", gap: 10 }}>
                    {[["Portrait (9:16)", "📱 Portrait (9:16)"], ["Landscape (16:9)", "🖥 Landscape (16:9)"]].map(([v, l]) => (
                      <button key={v} onClick={() => set("orientation", v)} style={{
                        flex: 1, padding: "11px 8px", borderRadius: 14, fontSize: 12, fontWeight: 700,
                        border: `1.5px solid ${form.orientation === v ? pk : bd}`,
                        background: form.orientation === v ? "linear-gradient(135deg,#FFF0F7,#F3E8FF)" : "#fff",
                        color: form.orientation === v ? pk : ts,
                        cursor: "pointer", fontFamily: "'Nunito',sans-serif", transition: "all 0.15s",
                      }}>{l}</button>
                    ))}
                  </div>
                </div>

                {/* Video Length */}
                <div style={{ marginBottom: 4 }}>
                  <label style={lbl}>Video Length</label>
                  <div style={{ display: "flex", gap: 10 }}>
                    {[["Short (~40 sec)", "⚡ Short (~40 sec)"], ["Long (~2 min)", "🎬 Long (~2 min)"]].map(([v, l]) => (
                      <button key={v} onClick={() => set("length", v)} style={{
                        flex: 1, padding: "11px 8px", borderRadius: 14, fontSize: 12, fontWeight: 700,
                        border: `1.5px solid ${form.length === v ? pk : bd}`,
                        background: form.length === v ? "linear-gradient(135deg,#FFF0F7,#F3E8FF)" : "#fff",
                        color: form.length === v ? pk : ts,
                        cursor: "pointer", fontFamily: "'Nunito',sans-serif", transition: "all 0.15s",
                      }}>{l}</button>
                    ))}
                  </div>
                </div>

                {/* India Pricing */}
                <PricingSection />

                {/* Delivery */}
                <div style={{ marginBottom: 16 }}>
                  <label style={lbl}>Delivery Speed</label>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {[
                      ["Standard (5–7 Days)", "🐢 Standard (5–7 Days)"],
                      ["Custom Date", "📅 Custom Date"],
                      ...(emergencyAvailable ? [["Emergency (24–48 Hours)", "⚡ Emergency (24–48 Hours)"]] : []),
                    ].map(([v, l]) => (
                      <button key={v} onClick={() => set("delivery", v)} style={{
                        padding: "11px 16px", borderRadius: 14, fontSize: 13, fontWeight: 700,
                        textAlign: "left", border: `1.5px solid ${form.delivery === v ? pk : bd}`,
                        background: form.delivery === v ? "linear-gradient(135deg,#FFF0F7,#F3E8FF)" : "#fff",
                        color: form.delivery === v ? pk : ts,
                        cursor: "pointer", fontFamily: "'Nunito',sans-serif", transition: "all 0.15s",
                      }}>{l}</button>
                    ))}
                  </div>
                  {form.delivery === "Emergency (24–48 Hours)" && (
                    <div style={{
                      background: "#FFF3E0", border: "1px solid #FFB74D", borderRadius: 10,
                      padding: "10px 14px", marginTop: 8, fontSize: 12, color: "#E07B00",
                    }}>
                      ⚡ Emergency delivery is subject to availability. We will confirm within 2 hours of your submission.
                      {emergencyCountdown && (
                        <div style={{ marginTop: 6, fontWeight: 800, fontSize: 13 }}>
                          ⚡ Emergency booking closes in {emergencyCountdown}
                        </div>
                      )}
                    </div>
                  )}
                  {form.delivery === "Custom Date" && (
                    <input
                      type="date" value={form.customDate}
                      onChange={e => set("customDate", e.target.value)}
                      style={{ ...inp, marginTop: 10 }}
                      min={nowISO()}
                    />
                  )}
                  {emergencyBlocked && (
                    <div style={{ background:"#FEF2F2", border:"1.5px solid #FECACA", borderRadius:12, padding:"10px 14px", marginTop:8, color:"#DC2626", fontSize:13, fontWeight:700 }}>
                      ⚠️ Emergency delivery is not available for the next 48 hours. Please select Standard Delivery or a Custom Date.
                    </div>
                  )}
                </div>

                {/* Language */}
                <div>
                  <label style={lbl}>Primary Language of Video</label>
                  <div style={{ display: "flex", gap: 10 }}>
                    {LANGUAGES.map(lang => (
                      <button key={lang} onClick={() => set("language", lang)} style={{
                        flex: 1, padding: "11px 8px", borderRadius: 14, fontSize: 13, fontWeight: 700,
                        border: `1.5px solid ${form.language === lang ? pk : bd}`,
                        background: form.language === lang ? "linear-gradient(135deg,#FFF0F7,#F3E8FF)" : "#fff",
                        color: form.language === lang ? pk : ts,
                        cursor: "pointer", fontFamily: "'Nunito',sans-serif", transition: "all 0.15s",
                      }}>
                        {lang}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: ts, marginTop: 6, lineHeight: 1.5 }}>
                    Want lines from another language? Send a voice note for pronunciation via WhatsApp.
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Btn onClick={() => animateStepComplete(0)} disabled={!canStep0}>Continue →</Btn>
              </div>
            </div>
          )}

          {/* ─── STEP 1 · SCRIPT ─── */}
          {step === 1 && (
            <div>
              <div style={card}>
                <div style={{ fontSize: 12, fontWeight: 900, color: pk, marginBottom: 14, letterSpacing: 0.5 }}>
                  ✍️ STEP 2 · SCRIPT
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={lbl}>Do you have a script?</label>
                  <div style={{ display: "flex", gap: 10 }}>
                    {[["write", "✍️ I'll write it"], ["help", "💬 Help me write it"]].map(([v, l]) => (
                      <button key={v} onClick={() => set("scriptChoice", v)} style={{
                        flex: 1, padding: "12px 8px", borderRadius: 14, fontSize: 13, fontWeight: 700,
                        border: `1.5px solid ${form.scriptChoice === v ? pk : bd}`,
                        background: form.scriptChoice === v ? "linear-gradient(135deg,#FFF0F7,#F3E8FF)" : "#fff",
                        color: form.scriptChoice === v ? pk : ts,
                        cursor: "pointer", fontFamily: "'Nunito',sans-serif", transition: "all 0.15s",
                      }}>{l}</button>
                    ))}
                  </div>
                </div>

                {form.scriptChoice === "write" && (
                  <div>
                    <div style={{
                      background: "#FFF0F7", borderRadius: 12, padding: "10px 14px",
                      marginBottom: 10, fontSize: 12, color: ts, lineHeight: 1.7, border: `1px dashed ${pk}`,
                    }}>
                      <strong>Include:</strong> Background setting · Dialogues · Activities (optional) · Expressions (optional)
                    </div>
                    <textarea
                      value={form.scriptText}
                      onChange={e => set("scriptText", e.target.value)}
                      placeholder="Write your script here…"
                      rows={8} style={{ ...inp, resize: "vertical", lineHeight: 1.6 }}
                    />
                  </div>
                )}

                {form.scriptChoice === "help" && (
                  <div>
                    <div style={{
                      background: "linear-gradient(135deg,#FFF0F7,#F3E8FF)", borderRadius: 14,
                      padding: "12px 16px", marginBottom: 16, fontSize: 13, color: ts,
                      lineHeight: 1.7, border: `1px solid ${bd}`,
                    }}>
                      <strong>Don't worry! ✨</strong><br />
                      Share your idea and we'll write the script for you. Once you approve it, we'll start animating!
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <label style={lbl}>Occasion</label>
                      <div style={{ display: "flex", flexWrap: "wrap" }}>
                        {OCCASIONS.map(o => <Tag key={o} label={o} active={form.occasions.includes(o)} onClick={() => tog("occasions", o)} />)}
                      </div>
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <label style={lbl}>Short Idea</label>
                      <div style={{ display: "flex", gap: 8, overflowX: "auto", marginBottom: 8, scrollbarWidth: "none", paddingBottom: 2 }}>
                        {[
                          "Proposal at our favourite café",
                          "Birthday surprise — she loves sunflowers",
                          "Long distance love story",
                        ].map(example => (
                          <button key={example} onClick={() => set("shortIdea", example)} style={{
                            flexShrink: 0, padding: "6px 12px", borderRadius: 20,
                            border: `1.5px dashed ${pk}`, background: "transparent",
                            color: pk, fontSize: 11, fontWeight: 700, cursor: "pointer",
                            fontFamily: "'Nunito',sans-serif", whiteSpace: "nowrap",
                          }}>💡 {example}</button>
                        ))}
                      </div>
                      <textarea
                        value={form.shortIdea}
                        onChange={e => set("shortIdea", e.target.value)}
                        placeholder="What's the main idea for the video?"
                        rows={3} style={{ ...inp, resize: "vertical" }}
                      />
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <label style={lbl}>Things You Enjoy Doing Together</label>
                      <div style={{ display: "flex", flexWrap: "wrap" }}>
                        {ACTIVITIES.map(a => <Tag key={a} label={a} active={form.activities.includes(a)} onClick={() => tog("activities", a)} />)}
                      </div>
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <label style={lbl}>Your Story</label>
                      <textarea
                        value={form.relationshipStory}
                        onChange={e => set("relationshipStory", e.target.value)}
                        placeholder="How you met, special moments, your journey together…"
                        rows={4} style={{ ...inp, resize: "vertical" }}
                      />
                      <div style={{ fontSize: 11, color: ts, marginTop: 4 }}>
                        Tip: 3-5 sentences is perfect. Include how you met, a special moment, and what makes your relationship unique.
                      </div>
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <label style={lbl}>Mood</label>
                      <div style={{ display: "flex", flexWrap: "wrap" }}>
                        {MOODS.map(m => <Tag key={m} label={m} active={form.mood === m} onClick={() => set("mood", m)} />)}
                      </div>
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <label style={lbl}>Background Preference</label>
                      <input
                        value={form.backgroundPref}
                        onChange={e => set("backgroundPref", e.target.value)}
                        placeholder="e.g. beach, cozy café, night sky…" style={inp}
                      />
                    </div>
                  </div>
                )}

                {/* Personalization — only for "help me write it" */}
                {form.scriptChoice === "help" && (
                  <div style={{ paddingTop: 16, borderTop: `1px dashed ${bd}`, marginTop: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 900, color: pk, letterSpacing: 0.5, marginBottom: 12 }}>
                      👤 CHARACTER PERSONALIZATION (Optional)
                    </div>
                    <div style={{ fontSize: 12, color: ts, marginBottom: 12, lineHeight: 1.6 }}>
                      Helps us write personalized dialogue for your video characters.
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={lbl}>Your Name</label>
                      <input value={form.yourName} onChange={e => set("yourName", e.target.value)}
                        placeholder="Your name (for character dialogue)…" style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>Partner Name</label>
                      <input value={form.partnerName} onChange={e => set("partnerName", e.target.value)}
                        placeholder="Your partner's name…" style={inp} />
                    </div>
                  </div>
                )}

                {/* Scene Style + References — only for "help me write it" */}
                {form.scriptChoice === "help" && (
                  <div style={{ paddingTop: 16, borderTop: `1px dashed ${bd}`, marginTop: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 900, color: pk, letterSpacing: 0.5, marginBottom: 12 }}>
                      🎭 SCENE STYLE PREFERENCE (Optional)
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", marginBottom: 14 }}>
                      {SCENE_STYLES.map(s => <Tag key={s} label={s} active={form.sceneStyle === s} onClick={() => set("sceneStyle", s)} />)}
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={lbl}>Reference Link (Google Docs / Apple Notes)</label>
                      <input value={form.referenceLink} onChange={e => set("referenceLink", e.target.value)}
                        placeholder="https://docs.google.com/…" style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>Reference Notes / Plain Text</label>
                      <textarea value={form.referenceText} onChange={e => set("referenceText", e.target.value)}
                        placeholder="Paste any reference text, ideas, or extra notes here…"
                        rows={3} style={{ ...inp, resize: "vertical" }} />
                    </div>
                  </div>
                )}

                {/* Reference Link only — for "I'll write it" */}
                {form.scriptChoice === "write" && (
                  <div style={{ paddingTop: 16, borderTop: `1px dashed ${bd}`, marginTop: 8 }}>
                    <label style={lbl}>Reference Link (Google Docs / Apple Notes or any file)</label>
                    <input value={form.referenceLink} onChange={e => set("referenceLink", e.target.value)}
                      placeholder="Attach your script externally (optional)…" style={inp} />
                    <div style={{ fontSize: 11, color: ts, marginTop: 5, lineHeight: 1.5 }}>
                      Paste a Google Docs, Apple Notes, or any link to your script for reference.
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Btn secondary onClick={() => setStep(0)}>← Back</Btn>
                <Btn onClick={() => animateStepComplete(1)} disabled={!canStep1}>Continue →</Btn>
              </div>
            </div>
          )}

          {/* ─── STEP 2 · CONTACT ─── */}
          {step === 2 && (
            <div>
              <div style={card}>
                <div style={{ fontSize: 12, fontWeight: 900, color: pk, marginBottom: 14, letterSpacing: 0.5 }}>
                  📬 STEP 3 · CONTACT INFO
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={lbl}>Your Name</label>
                  <input value={form.name} onChange={e => set("name", e.target.value)}
                    placeholder="Enter your name…" style={inp} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={lbl}>📧 Email Address <span style={{ color: pk }}>*</span></label>
                  <input type="email" value={form.email} onChange={e => set("email", e.target.value)}
                    placeholder="your@email.com"
                    style={{ ...inp, borderColor: emailError ? "#DC2626" : bd }} />
                  {emailError
                    ? <div style={{ fontSize: 11, color: "#DC2626", marginTop: 4, fontWeight: 700 }}>{emailError}</div>
                    : <div style={{ fontSize: 11, color: ts, marginTop: 4 }}>Order confirmation will be sent here automatically</div>
                  }
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={lbl}>📱 WhatsApp Number *</label>
                  <input value={form.whatsapp} onChange={e => set("whatsapp", e.target.value)}
                    onBlur={() => setWaTouched(true)}
                    placeholder="Digits only, e.g. 9876543210"
                    style={{ ...inp, borderColor: whatsappError ? "#DC2626" : bd }} />
                  {whatsappError
                    ? <div style={{ color:"#DC2626", fontSize:12, marginTop:4 }}>{whatsappError}</div>
                    : <div style={{ fontSize: 11, color: ts, marginTop: 4 }}>Required — we contact you about your order via WhatsApp</div>
                  }
                </div>
                <div>
                  <label style={lbl}>📸 Instagram Username</label>
                  <input value={form.instagram} onChange={e => set("instagram", e.target.value)}
                    placeholder="@yourhandle" style={inp} />
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Btn secondary onClick={() => setStep(1)}>← Back</Btn>
                <Btn onClick={() => animateStepComplete(2)} disabled={!canStep2}>Continue →</Btn>
              </div>
            </div>
          )}

          {/* ─── STEP 3 · REVIEW & SUBMIT ─── */}
          {step === 3 && (
            <div>
              {/* Summary */}
              <div style={{ ...card, background: "linear-gradient(135deg,#FFF0F7,#F3E8FF)" }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: pk, marginBottom: 12, letterSpacing: 0.5 }}>
                  📋 ORDER SUMMARY
                </div>
                {[
                  ["Country", form.country],
                  ["Orientation", form.orientation],
                  ["Length", form.length],
                  ["Delivery", form.delivery + (form.customDate ? ` · ${form.customDate}` : "")],
                  ["Language", form.language],
                  ["Script", form.scriptChoice === "write" ? "You wrote it" : "Needs help writing"],
                  ...(form.sceneStyle ? [["Style", form.sceneStyle]] : []),
                  ...(form.yourName ? [["Your Name", form.yourName]] : []),
                  ...(form.partnerName ? [["Partner Name", form.partnerName]] : []),
                  ...(form.mood ? [["Mood", form.mood]] : []),
                  ["Name", form.name],
                  ["Email", form.email],
                  ...(form.whatsapp ? [["WhatsApp", form.whatsapp]] : []),
                  ...(form.instagram ? [["Instagram", form.instagram]] : []),
                ].map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 7, gap: 12 }}>
                    <span style={{ color: ts, fontWeight: 600 }}>{k}</span>
                    <span style={{ color: tm, fontWeight: 700, textAlign: "right", flex: 1 }}>{v}</span>
                  </div>
                ))}
              </div>

              {/* Wallpaper Upsell */}
              <div style={{ border:"2px dashed #C084FC", borderRadius:16, padding:"14px 18px", marginBottom:14, background:"#FAF5FF" }}>
                <label style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer" }}>
                  <input type="checkbox" checked={upsellSelected} onChange={e => setUpsellSelected(e.target.checked)}
                    style={{ marginTop:2, accentColor:"#7C3AED", width:16, height:16, flexShrink:0 }} />
                  <div>
                    <div style={{ fontWeight:800, color:"#6D28D9", fontSize:14, marginBottom:3 }}>🖼️ Add Custom Wallpaper — +$5 USD</div>
                    <div style={{ fontSize:12, color:"#7C3AED", lineHeight:1.5 }}>
                      Get a custom Bubu Dudu wallpaper featuring you & your partner as cartoon characters. Perfect as a phone or desktop wallpaper!
                    </div>
                  </div>
                </label>
              </div>

              {/* Terms */}
              <div style={{ ...card, background: "#FFF8F0", border: "1.5px solid #FFD6A0" }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: "#E07B00", marginBottom: 10, letterSpacing: 0.5 }}>
                  📜 PLEASE READ BEFORE SUBMITTING
                </div>
                <div style={{ fontSize: 12.5, color: ts, lineHeight: 1.9 }}>
                  {[
                    "This is a request submission only. The project begins after we agree on the final script.",
                    "If you cannot write a script, share your idea — we'll create it for you to review.",
                    "Some specific animations may not be possible. Suitable alternatives will be used.",
                    "If the story requires a longer video, a small additional charge may apply.",
                    "Urgent delivery depends on availability.",
                    "Communication will happen via Instagram, email, or WhatsApp.",
                    "Payment: 50% after script approval · 50% after final delivery.",
                  ].map((pt, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, marginBottom: 7, alignItems: "flex-start" }}>
                      <span style={{ color: "#E07B00", fontWeight: 900, flexShrink: 0 }}>•</span>
                      <span>{pt}</span>
                    </div>
                  ))}
                </div>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginTop: 14 }}>
                  <input type="checkbox" checked={form.agreed} onChange={e => set("agreed", e.target.checked)}
                    style={{ marginTop: 3, width: 16, height: 16, accentColor: pk, cursor: "pointer", flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: tm, fontWeight: 700, lineHeight: 1.5 }}>
                    I have read and agree to all the above terms ✨
                  </span>
                </label>
              </div>

              {/* Private Video Policy */}
              <div style={{ ...card, background: "#F0F4FF", border: "1.5px solid #B0C4FF" }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: "#3B5BDB", marginBottom: 10, letterSpacing: 0.5 }}>
                  🔐 PRIVATE VIDEO POLICY
                </div>
                <div style={{ fontSize: 12.5, color: "#3D4B7A", lineHeight: 1.85 }}>
                  <div style={{ marginBottom: 8 }}>This video will be delivered only to you via:</div>
                  {["Instagram", "WhatsApp", "Google Drive downloadable link"].map(pt => (
                    <div key={pt} style={{ display: "flex", gap: 8, marginBottom: 5, alignItems: "center" }}>
                      <span style={{ color: "#3B5BDB", fontWeight: 900 }}>•</span>{pt}
                    </div>
                  ))}
                  <div style={{ marginTop: 10, lineHeight: 1.75 }}>
                    The video will <strong>NOT</strong> be posted on our page. This is a private custom project created only for you.
                    <br /><br />
                    If you want us to post it on our Instagram <strong>@dudu_phudu</strong>, an extra charge may apply and it depends on slot availability.
                  </div>
                </div>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginTop: 14 }}>
                  <input type="checkbox" checked={form.agreedPrivacy} onChange={e => set("agreedPrivacy", e.target.checked)}
                    style={{ marginTop: 3, width: 16, height: 16, accentColor: "#3B5BDB", cursor: "pointer", flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: "#3D4B7A", fontWeight: 700, lineHeight: 1.5 }}>
                    I understand and agree to the Private Video Policy ✨
                  </span>
                </label>
              </div>

              {/* Security Assurance */}
              <div style={{ ...card, background: "#F0FFF4", border: "1.5px solid #86EFAC" }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: "#16A34A", marginBottom: 10, letterSpacing: 0.5 }}>
                  🔒 SAFE & SECURE CUSTOM PROJECT
                </div>
                {[
                  "Your story stays private",
                  "You approve script before animation",
                  "Preview provided before final delivery",
                ].map(pt => (
                  <div key={pt} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}>
                    <span style={{ color: "#16A34A", fontSize: 15, flexShrink: 0 }}>✓</span>
                    <span style={{ fontSize: 12.5, color: "#166534", fontWeight: 600 }}>{pt}</span>
                  </div>
                ))}
              </div>

              {error && (
                <div id="submit-error" style={{
                  background: "#FEF2F2", border: "1px solid #DC2626", borderRadius: 12,
                  padding: "10px 14px", fontSize: 13, color: "#DC2626",
                  fontWeight: 700, marginBottom: 14,
                }}>
                  ⚠️ {error}
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Btn secondary onClick={() => setStep(2)}>← Back</Btn>
                <Btn onClick={handleSubmit} disabled={!canSubmit}>
                  {saving ? "Submitting… ✨" : "Submit Request 🎀"}
                </Btn>
              </div>
            </div>
          )}

          <div style={{ textAlign: "center", marginTop: 28, fontSize: 12, color: "#C0A8B5" }}>
            Made with 💗 by Bubu Dudu ·{" "}
            <a href="https://instagram.com/dudu_phudu" style={{ color: pk }}>@dudu_phudu</a>
          </div>
        </div>
      )}

      {/* ══════════ TRACK ORDER PAGE ══════════ */}
      {page === "track" && (
        <div style={{ maxWidth: 520, margin: "0 auto", padding: "24px 16px 60px" }}>

          {!isOnline && (
            <div style={{
              background: "#FFF8E1", border: "1px solid #FFD54F", borderRadius: 12,
              padding: "10px 14px", marginBottom: 16, textAlign: "center",
              fontSize: 13, fontWeight: 700, color: "#E07B00",
            }}>📡 Offline — showing last saved data</div>
          )}

          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={{ fontSize: 36, marginBottom: 4 }}>📍</div>
            <h1 style={{
              fontFamily: "'Fredoka One',cursive", fontSize: 24, margin: "0 0 6px",
              background: `linear-gradient(135deg,${pk},${pu})`,
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>Track Your Order</h1>
            <p style={{ color: ts, fontSize: 13, margin: 0, fontWeight: 600 }}>
              Check your custom video order status anytime
            </p>
          </div>

          {/* Search Card */}
          <div style={card}>
            <div style={{ fontSize: 12, fontWeight: 900, color: pk, marginBottom: 10, letterSpacing: 0.5 }}>
              🔍 FIND YOUR ORDER
            </div>
            <div style={{ fontSize: 12.5, color: ts, marginBottom: 12, lineHeight: 1.6 }}>
              Enter your Email, WhatsApp number, or Order ID (e.g. BD-12345) to check your order status.
            </div>
            <input
              value={trackSearch}
              onChange={e => setTrackSearch(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleTrackSearch()}
              placeholder="Email, WhatsApp number, or Order ID…"
              style={{ ...inp, marginBottom: 12 }}
            />
            <button
              onClick={handleTrackSearch}
              disabled={trackLoading || !trackSearch.trim()}
              style={{
                width: "100%", padding: "13px", borderRadius: 50,
                background: !trackSearch.trim() || trackLoading ? "#E8D5DF" : `linear-gradient(135deg,${pk},${pu})`,
                color: !trackSearch.trim() || trackLoading ? "#C0A8B5" : "#fff",
                fontWeight: 800, fontSize: 14, border: "none",
                cursor: !trackSearch.trim() || trackLoading ? "not-allowed" : "pointer",
                fontFamily: "'Nunito',sans-serif",
                boxShadow: !trackSearch.trim() ? "none" : "0 4px 18px rgba(255,107,157,0.3)",
              }}
            >
              {trackLoading ? "Searching… 🔍" : "Check Order Status"}
            </button>
            {trackError && (
              <div style={{
                marginTop: 12, background: "#FEF2F2", border: "1px solid #DC2626",
                borderRadius: 10, padding: "10px 14px", fontSize: 13,
                color: "#DC2626", fontWeight: 700,
              }}>{trackError}</div>
            )}
          </div>

          {/* Order List (multiple results) */}
          {trackResults && !selectedOrder && (
            <div style={card}>
              {trackResults.length >= 2 && (
                <div style={{
                  background: "linear-gradient(135deg,#FFF0F7,#F3E8FF)",
                  borderRadius: 12, padding: "10px 14px", marginBottom: 12,
                  border: `1px solid ${bd}`, textAlign: "center",
                }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: pk }}>
                    💕 Welcome back! You've ordered {trackResults.length} times with us. Thank you! 🎀
                  </span>
                </div>
              )}
              <div style={{ fontSize: 12, fontWeight: 900, color: pk, marginBottom: 12, letterSpacing: 0.5 }}>
                📋 ORDERS FOUND
              </div>
              {trackResults.map(order => (
                <button
                  key={order.id}
                  onClick={() => { setSelectedOrder(order); setRating(0); setReviewText(""); setRatingSubmitted(false); setEditing(false); }}
                  style={{
                    width: "100%", background: "#FFF0F7", border: `1.5px solid ${bd}`,
                    borderLeft: `4px solid ${STATUS_INFO[order.status]?.color || "#FF6B9D"}`,
                    borderRadius: 14, padding: "12px 16px", marginBottom: 8,
                    cursor: "pointer", fontFamily: "'Nunito',sans-serif",
                    textAlign: "left", transition: "all 0.15s",
                  }}
                >
                  <div style={{ fontFamily: "'Fredoka One',cursive", fontSize: 18, color: pk }}>
                    {order.id}
                  </div>
                  <div style={{ fontSize: 12, color: ts, marginTop: 2 }}>
                    Submitted: {order.submittedAt || order.date}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <span style={{
                      background: `linear-gradient(135deg,${pk},${pu})`,
                      color: "#fff", borderRadius: 20, padding: "3px 10px",
                      fontSize: 11, fontWeight: 800,
                    }}>{order.status}</span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!trackResults && !selectedOrder && !trackLoading && !trackError && (
            <div style={{ textAlign: "center", padding: "20px 0 8px" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
              <div style={{ fontSize: 13, color: ts, fontWeight: 600 }}>
                Enter your Order ID, email, or WhatsApp number above
              </div>
              <div style={{ fontSize: 12, color: ts, marginTop: 4 }}>
                Example: BD-M8K24521
              </div>
            </div>
          )}

          {/* Order Details */}
          {selectedOrder && <OrderDetailsView order={selectedOrder} />}

          {/* Example Reels */}
          <ExampleReelsSection />

          {/* Testimonials */}
          <TestimonialsSection />

          {/* FAQ */}
          <FAQSection />

          {/* Contact Support */}
          <div style={{ ...card, textAlign: "center", background: "linear-gradient(135deg,#FFF0F7,#F3E8FF)", marginTop: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: ts, marginBottom: 8 }}>
              Need help with your order?
            </div>
            <a href="https://instagram.com/dudu_phudu" target="_blank" rel="noreferrer"
              style={{ color: pk, fontWeight: 800, fontSize: 14, textDecoration: "none" }}>
              📸 Message us on Instagram @dudu_phudu
            </a>
          </div>

          <div style={{ textAlign: "center", marginTop: 24, fontSize: 12, color: "#C0A8B5" }}>
            Made with 💗 by Bubu Dudu ·{" "}
            <a href="https://instagram.com/dudu_phudu" style={{ color: pk }}>@dudu_phudu</a>
          </div>
        </div>
      )}
    </div>
  );
}
